import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API_ROOT } from "@/lib/api";
import { Upload, FileSpreadsheet, Play, CheckCircle2, AlertCircle, Loader2, ChevronRight, Database, Info, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function ImportExcelPage() {
    const qc = useQueryClient();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [lastReport, setLastReport] = useState(null);

    const schemaQ = useQuery({
        queryKey: ["import-schema"],
        queryFn: () => api.get("/admin/import/template-schema").then((r) => r.data),
    });
    const runsQ = useQuery({
        queryKey: ["import-runs"],
        queryFn: () => api.get("/admin/import/runs").then((r) => r.data),
    });

    const onFileChosen = (f) => {
        if (!f) return;
        if (!/\.(xlsx|xlsm)$/i.test(f.name)) {
            toast.error("Formato non valido. Servono .xlsx o .xlsm");
            return;
        }
        setFile(f);
        setPreview(null);
        setLastReport(null);
        doPreview(f);
    };

    const doPreview = async (f) => {
        setPreviewing(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const token = localStorage.getItem("pv_token");
            const res = await fetch(`${API_ROOT}/admin/import/preview`, {
                method: "POST", body: fd, credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("preview failed");
            const data = await res.json();
            setPreview(data);
            if (!data.master_sheet_found) {
                toast.error(`Foglio 'CRM Master' non trovato. Fogli: ${data.sheets?.join(", ") || "?"}`);
            } else {
                toast.success(`Anteprima OK — ${data.master_total_rows} righe rilevate nel foglio CRM Master.`);
            }
        } catch (e) {
            toast.error("Errore lettura file: " + (e?.message || ""));
        } finally {
            setPreviewing(false);
        }
    };

    const doImport = async () => {
        if (!file) return;
        if (!window.confirm(`Confermi l'import di ${preview?.master_total_rows ?? "?"} righe? Verranno creati/aggiornati contatti secondo la dedup priority (phone → email → dedup key → nome+città).`)) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const token = localStorage.getItem("pv_token");
            const res = await fetch(`${API_ROOT}/admin/import/crm-excel`, {
                method: "POST", body: fd, credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const report = await res.json();
            setLastReport(report);
            qc.invalidateQueries({ queryKey: ["import-runs"] });
            qc.invalidateQueries({ queryKey: ["contacts"] });
            toast.success(`Import completato: ${report.contacts_created} nuovi, ${report.contacts_merged} mergiati`);
        } catch (e) {
            toast.error("Errore import: " + (e?.message || ""));
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="space-y-6 pv-enter">
            <div>
                <div className="pv-eyebrow mb-2 flex items-center gap-2">
                    <Database size={11} strokeWidth={1.5} /> Admin · Import dati reali
                </div>
                <h1 className="pv-serif text-4xl tracking-tight">Import Excel CRM</h1>
                <p className="text-[13.5px] text-textmuted mt-2 max-w-2xl">
                    Carica il workbook <span className="pv-mono">CRM Master KER Prime Vision.xlsx</span> per popolare contatti reali,
                    interazioni, task e provenance. Nessun dato viene inventato: se un campo non esiste nel file,
                    verrà marcato come <span className="pv-mono text-textmain">"non presente nel file"</span>.
                </p>
            </div>

            {/* Step 1 — Upload */}
            <StepCard num={1} title="Carica il file Excel" active={!file}>
                <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        const f = e.dataTransfer.files?.[0]; onFileChosen(f);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="import-dropzone"
                    className="border-2 border-dashed border-border/[var(--border-opacity)] hover:border-primary/40 rounded-xl px-8 py-10 text-center cursor-pointer transition-colors hover:bg-primary/[0.02]"
                >
                    <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                        <FileSpreadsheet size={20} strokeWidth={1.5} className="text-primary" />
                    </div>
                    <div className="text-[14px] font-medium">Trascina qui il file .xlsx</div>
                    <div className="text-[12px] text-textsubtle mt-1">oppure clicca per selezionare · max 25MB</div>
                    <input
                        ref={fileInputRef}
                        data-testid="import-file-input"
                        type="file"
                        accept=".xlsx,.xlsm"
                        className="hidden"
                        onChange={(e) => onFileChosen(e.target.files?.[0])}
                    />
                </div>
                {file && (
                    <div className="mt-3 flex items-center gap-2 text-[12.5px] text-textmuted">
                        <FileSpreadsheet size={13} strokeWidth={1.5} className="text-primary" />
                        <span className="font-medium text-textmain">{file.name}</span>
                        <span>·</span>
                        <span>{(file.size / 1024).toFixed(1)} KB</span>
                        <button
                            onClick={() => { setFile(null); setPreview(null); setLastReport(null); fileInputRef.current.value = ""; }}
                            className="ml-auto text-[11.5px] text-critical hover:underline"
                        >Rimuovi</button>
                    </div>
                )}
            </StepCard>

            {/* Step 2 — Preview */}
            {file && (
                <StepCard num={2} title="Anteprima e mapping colonne" active={!!preview && !lastReport}>
                    {previewing && <PreviewLoading />}
                    {preview && <PreviewPanel preview={preview} />}
                </StepCard>
            )}

            {/* Step 3 — Import */}
            {preview?.master_sheet_found && (
                <StepCard num={3} title="Esegui import" active={!!preview && !lastReport}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="text-[12.5px] text-textmuted max-w-xl">
                            Sto per creare/aggiornare contatti da <b className="text-textmain">{preview.master_total_rows}</b> righe.
                            La deduplica userà nell'ordine: telefono normalizzato → email → dedup key → nome+città → ID KER.
                            Nessuna fonte verrà cancellata: i duplicati sono uniti conservando la provenance.
                        </div>
                        <button
                            onClick={doImport}
                            disabled={importing}
                            data-testid="import-run-btn"
                            className="flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-4 py-2.5 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
                        >
                            {importing ? <Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> : <Play size={14} strokeWidth={1.5} />}
                            {importing ? "Import in corso..." : "Importa nel database"}
                        </button>
                    </div>
                </StepCard>
            )}

            {/* Report */}
            {lastReport && <ReportPanel report={lastReport} />}

            {/* Schema */}
            <SchemaPanel schema={schemaQ.data} />

            {/* Past runs */}
            <RunsPanel runs={runsQ.data || []} isLoading={runsQ.isLoading} onRefresh={() => runsQ.refetch()} />
        </div>
    );
}

function StepCard({ num, title, active, children }) {
    return (
        <div className={`pv-card p-6 pv-hover ${active ? "ring-1 ring-primary/20" : ""}`}>
            <div className="flex items-center gap-3 mb-4">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-medium ${
                    active ? "bg-primary text-primary-foreground" : "bg-surface2 text-textmuted"
                }`}>{num}</div>
                <h2 className="text-[15px] font-medium">{title}</h2>
            </div>
            {children}
        </div>
    );
}

function PreviewLoading() {
    return (
        <div className="flex items-center gap-2 text-[13px] text-textmuted py-6">
            <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
            Sto leggendo il file…
        </div>
    );
}

function PreviewPanel({ preview }) {
    if (!preview.master_sheet_found) {
        return (
            <div className="p-4 rounded-xl border border-critical/40 bg-critical/[0.05] text-critical text-[13px] flex items-start gap-2">
                <AlertCircle size={14} strokeWidth={1.5} className="mt-0.5" />
                <div>
                    <div className="font-medium">Foglio "CRM Master" non trovato</div>
                    <div className="text-[12px] mt-1 opacity-90">
                        Fogli disponibili nel file: <span className="pv-mono">{preview.sheets?.join(", ") || "—"}</span>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatChip label="Righe totali" value={preview.master_total_rows} tone="primary" />
                <StatChip label="Colonne mappate" value={preview.columns_present.length} tone="success" />
                <StatChip label="Colonne mancanti" value={preview.columns_missing.length} tone={preview.columns_missing.length ? "warning" : "success"} />
                <StatChip label="Fogli nel workbook" value={preview.sheets?.length ?? 0} tone="primary" />
            </div>

            <details className="pv-card p-4">
                <summary className="cursor-pointer text-[12.5px] font-medium">
                    Prime 20 righe (anteprima parsing)
                </summary>
                <div className="mt-3 overflow-x-auto pv-scroll">
                    <table className="text-[11.5px] w-full">
                        <thead>
                            <tr className="text-textsubtle text-left border-b border-border/[var(--border-opacity)]">
                                <th className="py-1.5 pr-3">#</th>
                                <th className="py-1.5 pr-3">Nome</th>
                                <th className="py-1.5 pr-3">Città</th>
                                <th className="py-1.5 pr-3">Phone</th>
                                <th className="py-1.5 pr-3">Email</th>
                                <th className="py-1.5 pr-3">Stato</th>
                                <th className="py-1.5 pr-3">Score</th>
                                <th className="py-1.5 pr-3">Fonte</th>
                                <th className="py-1.5 pr-3">Ultimo msg</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/[var(--border-opacity)]">
                            {preview.sample_rows.map((r, i) => (
                                <tr key={i}>
                                    <td className="py-1.5 pr-3 text-textsubtle">{r.source_provenance?.excel_row}</td>
                                    <td className="py-1.5 pr-3 font-medium">
                                        {[r.first_name, r.last_name].filter(Boolean).join(" ") || <span className="text-textsubtle">—</span>}
                                    </td>
                                    <td className="py-1.5 pr-3">{r.city || <span className="text-textsubtle">—</span>}</td>
                                    <td className="py-1.5 pr-3 pv-mono text-[11px]">{r.phone_normalized || <span className="text-textsubtle">—</span>}</td>
                                    <td className="py-1.5 pr-3 text-[11px]">{r.email || <span className="text-textsubtle">—</span>}</td>
                                    <td className="py-1.5 pr-3">{r.contact_state || <span className="text-textsubtle">—</span>}</td>
                                    <td className="py-1.5 pr-3 pv-mono">{r.score ?? "—"}</td>
                                    <td className="py-1.5 pr-3 text-[11px]">{r.original_source || <span className="text-textsubtle">—</span>}</td>
                                    <td className="py-1.5 pr-3 text-[11px] text-warning">{r.last_message_status || "non presente nel file"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>

            {preview.columns_missing.length > 0 && (
                <div className="p-3 rounded-xl border border-warning/30 bg-warning/[0.05] text-[12px] flex items-start gap-2">
                    <AlertCircle size={13} strokeWidth={1.5} className="mt-0.5 text-warning" />
                    <div>
                        <div className="text-textmain font-medium">Colonne mancanti (non bloccanti)</div>
                        <div className="text-textmuted mt-1">{preview.columns_missing.join(" · ")}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatChip({ label, value, tone }) {
    const cls = tone === "critical" ? "bg-critical/10 text-critical" :
                tone === "warning" ? "bg-warning/10 text-warning" :
                tone === "success" ? "bg-success/10 text-success" :
                "bg-primary/[0.08] text-primary";
    return (
        <div className={`rounded-xl p-3 ${cls}`}>
            <div className="text-[10.5px] uppercase tracking-wider opacity-80">{label}</div>
            <div className="text-2xl font-medium pv-serif mt-0.5">{value}</div>
        </div>
    );
}

function ReportPanel({ report }) {
    const ok = report.status === "completed";
    return (
        <div className="pv-card p-6 pv-enter" data-testid="import-report">
            <div className="flex items-center gap-2 mb-4">
                {ok ? <CheckCircle2 size={16} strokeWidth={1.5} className="text-success" />
                    : <AlertCircle size={16} strokeWidth={1.5} className="text-warning" />}
                <h2 className="text-[16px] font-medium">Report import · <span className="pv-mono text-[13px] text-textmuted">{report.filename}</span></h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatChip label="Contatti creati" value={report.contacts_created ?? 0} tone="success" />
                <StatChip label="Contatti mergiati" value={report.contacts_merged ?? 0} tone="primary" />
                <StatChip label="Interazioni create" value={report.interactions_created ?? 0} tone="primary" />
                <StatChip label="Task importati" value={report.tasks_created ?? 0} tone="primary" />
                <StatChip label="Duplicati registrati" value={report.duplicates_recorded ?? 0} tone="warning" />
                <StatChip label="Fonti registrate" value={report.sources_recorded ?? 0} tone="primary" />
                <StatChip label="Errori" value={report.errors?.length ?? 0} tone={report.errors?.length ? "critical" : "success"} />
                <StatChip label="Warnings" value={report.warnings?.length ?? 0} tone={report.warnings?.length ? "warning" : "success"} />
            </div>

            {report.errors?.length > 0 && (
                <details className="mt-4 pv-card p-3">
                    <summary className="cursor-pointer text-[12.5px] font-medium text-critical">
                        {report.errors.length} errori — clicca per espandere
                    </summary>
                    <div className="mt-2 space-y-1 text-[11.5px]">
                        {report.errors.slice(0, 30).map((e, i) => (
                            <div key={i} className="pv-mono text-textmuted">
                                riga {e.row ?? "—"}: {e.message}
                            </div>
                        ))}
                    </div>
                </details>
            )}
            {report.warnings?.length > 0 && (
                <details className="mt-3 pv-card p-3">
                    <summary className="cursor-pointer text-[12.5px] font-medium text-warning">
                        {report.warnings.length} warnings
                    </summary>
                    <div className="mt-2 space-y-1 text-[11.5px] text-textmuted pv-mono">
                        {report.warnings.slice(0, 30).map((w, i) => <div key={i}>{w}</div>)}
                    </div>
                </details>
            )}

            <div className="mt-4 pt-3 border-t border-border/[var(--border-opacity)] flex items-center justify-between text-[11.5px]">
                <div className="text-textsubtle">
                    Iniziato: {new Date(report.started_at).toLocaleString("it-IT")}
                    {report.duration_sec != null && ` · Durata ${report.duration_sec}s`}
                </div>
                <Link to="/contacts" className="text-primary hover:underline flex items-center gap-1">
                    Vai ai contatti importati <ChevronRight size={12} strokeWidth={1.5} />
                </Link>
            </div>
        </div>
    );
}

function SchemaPanel({ schema }) {
    if (!schema) return null;
    return (
        <details className="pv-card p-5">
            <summary className="cursor-pointer flex items-center gap-2 text-[13.5px] font-medium">
                <Info size={13} strokeWidth={1.5} className="text-primary" />
                Schema atteso · 39 colonne CRM Master
            </summary>
            <div className="mt-4 space-y-3">
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
                    {schema.expected_columns.map((c) => (
                        <div key={c.field} className="flex items-center gap-2 py-0.5">
                            <span className="text-textmain flex-1">{c.excel}</span>
                            <ChevronRight size={10} strokeWidth={1.5} className="text-textsubtle" />
                            <span className="pv-mono text-[11px] text-textmuted">{c.field}</span>
                        </div>
                    ))}
                </div>
                <div className="pv-card p-3 bg-primary/[0.03] text-[12px] space-y-1">
                    {schema.notes.map((n, i) => (
                        <div key={i} className="flex items-start gap-1.5"><span className="text-primary mt-0.5">•</span><span>{n}</span></div>
                    ))}
                </div>
                <div className="text-[11.5px] text-textsubtle">
                    Priorità dedup: <span className="pv-mono text-textmain">{schema.dedup_priority.join(" → ")}</span>
                </div>
                <div className="text-[11.5px] text-textsubtle">
                    Altri fogli importati: <span className="pv-mono text-textmain">{schema.other_sheets_imported.join(", ")}</span>
                </div>
            </div>
        </details>
    );
}

function RunsPanel({ runs, isLoading, onRefresh }) {
    return (
        <div className="pv-card p-5" data-testid="import-runs-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[13.5px] font-medium">
                    Storico import {isLoading ? (
                        <span className="text-textsubtle">(…)</span>
                    ) : (
                        <>({runs.length})</>
                    )}
                </div>
                <button onClick={onRefresh} className="h-7 w-7 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted" data-testid="import-runs-refresh">
                    <RefreshCw size={13} strokeWidth={1.5} className={isLoading ? "animate-spin" : ""} />
                </button>
            </div>
            {isLoading ? (
                <div className="flex items-center gap-2 text-[12.5px] text-textsubtle py-4" data-testid="import-runs-loading">
                    <Loader2 size={13} strokeWidth={1.5} className="animate-spin" />
                    Carico lo storico…
                </div>
            ) : runs.length === 0 ? (
                <div className="text-[12.5px] text-textsubtle py-4" data-testid="import-runs-empty">Nessun import ancora eseguito.</div>
            ) : (
                <div className="divide-y divide-border/[var(--border-opacity)]">
                    {runs.map((r) => (
                        <div key={r.run_id} className="py-3 flex items-center gap-3 text-[12.5px]">
                            {r.status === "completed"
                                ? <CheckCircle2 size={13} strokeWidth={1.5} className="text-success flex-shrink-0" />
                                : <AlertCircle size={13} strokeWidth={1.5} className="text-warning flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{r.filename}</div>
                                <div className="text-[10.5px] text-textsubtle pv-mono truncate">{r.run_id}</div>
                            </div>
                            <div className="hidden md:block text-[11px] text-textmuted">
                                {r.contacts_created ?? 0} nuovi · {r.contacts_merged ?? 0} merge · {r.tasks_created ?? 0} task
                            </div>
                            <div className="text-[11px] text-textsubtle">
                                {new Date(r.started_at).toLocaleString("it-IT")}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
